import type { FormatLocale, UiLanguage } from '#layers/base/types/locale';

/**
 * Mints a {@link UiLanguage} from a BCP-47 tag. The only sanctioned way to produce one, so
 * every value that reaches a catalog lookup can be traced back to a deliberate decision.
 */
export function toUiLanguage(tag: string): UiLanguage {
	return tag as UiLanguage;
}

/**
 * Mints a {@link FormatLocale} from a BCP-47 tag. The only sanctioned way to produce one, so
 * every value that reaches an `Intl` formatter can be traced back to a deliberate decision.
 */
export function toFormatLocale(tag: string): FormatLocale {
	return tag as FormatLocale;
}

let browserLocale: string | undefined;

/**
 * The locale the browser resolved for this user, canonicalised by `Intl`. Mirrors
 * {@link getLocalTimezone}, which is where the datetime blocks already get their zone.
 *
 * Resolving costs ~20µs and the answer cannot change without a reload, so it is memoised: every
 * date cell mounts a `useUserLocale()` and would otherwise pay for it again.
 */
export function getBrowserLocale(): string {
	return (browserLocale ??= Intl.DateTimeFormat().resolvedOptions().locale);
}

/**
 * Composes the tag handed to `Intl` from the language the UI is written in and the region the
 * browser reports, e.g. an English UI on a German browser formats as `en-DE`: English month
 * names, German ordering and number grouping.
 *
 * This is the split macOS and Windows expose as Language and Region. Deriving the format locale
 * from the UI language alone would hand every non-US user `1/10/2026`, which most of the world
 * reads as the tenth of January; taking the browser tag wholesale would put German month names
 * in an English UI.
 *
 * Falls back to the UI language when the browser reports no region subtag.
 *
 * The composed tag only carries region conventions that CLDR actually ships for that language:
 * `en-DE` resolves as itself, but `de-US` has no data and falls back to plain `de`, so a German UI
 * on a US machine keeps German number grouping. Harmless while one language ships; revisit when a
 * second one does.
 */
export function resolveFormatLocale(language: UiLanguage, browserLocale: string): FormatLocale {
	const region = new Intl.Locale(browserLocale).region;

	if (!region) return toFormatLocale(language);

	// Swapping the region through `Intl.Locale` keeps any script subtag: hand-assembling the tag
	// would turn `zh-Hant-TW` into `zh-DE` and silently drop the script.
	return toFormatLocale(new Intl.Locale(language, { region }).toString());
}
